import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useRoleUsers } from '../hooks/useRoleUsers';
import AppLayout from '../components/AppLayout';
import ProductTable from '../components/ProductTable';
import FilterBar from '../components/FilterBar';
import toast from 'react-hot-toast';
import { Product } from '../types/Product';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Package, FileSpreadsheet, AlertCircle } from 'lucide-react';

const VisualDashboard: React.FC = () => {
  const { user } = useAuth();
  const { getUsersByRole } = useRoleUsers();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    mainCategory: '',
    subCategory: '',
    department: '',
    priceStatus: '',
    stockStatus: '',
    dateRange: '',
    search: '',
    statusFilter: ''
  });

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/products');
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Ürünler yüklenemedi!');
      console.error('Visual load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Status filtresi - SADECE KART TIKLANINCA FİLTRELE
    // Not: Yeni akışta görsel tamamlanan ürün anında publish_pending'e çıkıyor.
    // Legacy kayıtlar (image_upload + uploadedExternally=true) Publish departmanının
    // işidir; Visual ekranı bunları göstermemeli (çift sahiplik önlenir).
    if (filters.statusFilter) {
      if (filters.statusFilter === 'waiting_image') {
        filtered = filtered.filter(
          p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
        );
      } else if (filters.statusFilter === 'nebim') {
        filtered = filtered.filter(p => p.status === 'production_entry' || p.status === 'nebim_entry');
      } else if (filters.statusFilter === 'all') {
        // Tüm ürünler - filtre yok
      } else {
        filtered = filtered.filter(p => p.status === filters.statusFilter);
      }
    } else {
      // Filtre yoksa sadece gerçek image_upload olanları göster (legacy hariç)
      filtered = filtered.filter(
        p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
      );
    }

    // Kategori filtreleri
    if (filters.mainCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.mainCategory === filters.mainCategory);
    }
    if (filters.subCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.subCategory === filters.subCategory);
    }

    // Arama filtresi
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(p => {
        const productName = (p.productionInfo?.productName || '').toLowerCase();
        const sku = (p.nebimInfo?.sku || '').toLowerCase();
        const manufacturer = (p.productionInfo?.manufacturer || '').toLowerCase();
        
        return productName.includes(search) || 
               sku.includes(search) || 
               manufacturer.includes(search);
      });
    }

    setFilteredProducts(filtered);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleImageUpload = async (productId: string) => {
    const product = products.find(p => p._id === productId);
    if (!product) return;
    
    try {
      await axios.post(`/products/${productId}/image-upload`, {
        imageUrls: ['Resimler harici olarak yüklendi'],
        qualityChecked: true,
        uploadedExternally: true
      });

      toast.success('✅ Görsel yükleme tamamlandı! Ürün yayınlama onayı bekliyor.');
      loadProducts();
    } catch (error) {
      toast.error('İşlem başarısız!');
      console.error('Image upload error:', error);
    }
  };

  const handleReject = async (productId: string, targetStep: string, reason: string) => {
    try {
      await axios.post(`/products/${productId}/reject`, {
        targetStep,
        reason,
        rejectedBy: user?.id
      });
      toast.success('Ürün başarıyla geri yollandı!');
      loadProducts();
    } catch (error) {
      toast.error('Geri yollama başarısız!');
      console.error('Reject error:', error);
      throw error;
    }
  };

  const handleClosureRequest = async (productId: string, reason: string) => {
    try {
      await axios.post(`/products/${productId}/closure-request`, { reason });
      toast.success('Kapatma talebi gönderildi!');
      loadProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Talep gönderilemedi!');
    }
  };

  // const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const files = e.target.files;
  //   if (files) {
  //     const imageFiles = Array.from(files).filter(file => 
  //       file.type.startsWith('image/')
  //     );
  //     
  //     if (imageFiles.length === 0) {
  //       toast.error('Lütfen sadece görsel dosyaları seçin!');
  //       return;
  //     }
  //     
  //     if (imageFiles.length + imageUrls.length > 10) {
  //       toast.error('En fazla 10 görsel yükleyebilirsiniz!');
  //       return;
  //     }
  //     
  //     setUploadedImages(prev => [...prev, ...imageFiles]);
  //     
  //     // Preview için geçici URL'ler oluştur
  //     const newUrls = imageFiles.map(file => URL.createObjectURL(file));
  //     setImageUrls(prev => [...prev, ...newUrls]);
  //   }
  // };

  // const handleRemoveImage = (index: number) => {
  //   setImageUrls(prev => prev.filter((_, i) => i !== index));
  //   // Eğer yeni yüklenen bir dosyaysa
  //   if (index >= (selectedProduct?.apiInfo?.imageUrls?.length || 0)) {
  //     const uploadedIndex = index - (selectedProduct?.apiInfo?.imageUrls?.length || 0);
  //     setUploadedImages(prev => prev.filter((_, i) => i !== uploadedIndex));
  //   }
  // };

  const handleCardClick = (status: string) => {
    if (filters.statusFilter === status) {
      setFilters(prev => ({ ...prev, statusFilter: '' }));
    } else {
      setFilters(prev => ({ ...prev, statusFilter: status }));
    }
  };

  const handleExcelExport = async () => {
    try {
      const headers = [
        'ID',
        'Ürün Adı',
        'Üretici',
        'SKU',
        'Görsel Sayısı',
        'Kalite Kontrolü',
        'Durum',
        'Kategori',
        'Güncelleme Tarihi'
      ];
      
      const rows = filteredProducts.map(p => {
        const category = [
          p.categoryInfo?.mainCategory,
          p.categoryInfo?.subCategory,
          p.categoryInfo?.detailCategory
        ].filter(Boolean).join(' > ');
        
        return [
          p._id,
          p.productionInfo?.productName || '',
          p.productionInfo?.manufacturer || '',
          p.nebimInfo?.sku || '',
          p.apiInfo?.imageUrls?.length || 0,
          p.apiInfo?.qualityChecked ? 'Evet' : 'Hayır',
          p.status,
          category || '',
          new Date(p.updatedAt).toLocaleDateString('tr-TR')
        ];
      });
      
      const BOM = '\uFEFF';
      const csvContent = BOM + [
        headers.join('\t'),
        ...rows.map(row => row.join('\t'))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `gorsel_urunler_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Excel dosyası indirildi!');
    } catch (error) {
      toast.error('Excel export başarısız!');
    }
  };

  const stats = {
    draft: products.filter(p => p.status === 'draft').length,
    nebim: products.filter(p => p.status === 'production_entry' || p.status === 'nebim_entry').length,
    barcode_sent: products.filter(p => p.status === 'barcode_sent').length,
    stock_entry: products.filter(p => p.status === 'stock_entry').length,
    api_transfer: products.filter(p => p.status === 'api_transfer').length,
    content_entry: products.filter(p => p.status === 'content_entry').length,
    waiting_image: products.filter(
      p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
    ).length,
    published: products.filter(p => p.status === 'published' && p.isActive !== false).length,
    total: products.length
  };

  return (
    <AppLayout
      title="Görsel Yönetimi Paneli"
      subtitle={`Ürün Fotoğrafı Yükleme & Kalite Kontrol - ${getUsersByRole('visual') || 'Görsel'}`}
    >
      <div className="container mx-auto py-8 space-y-8 px-4">
        {/* Gösterge Paneli */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-blue-900">Görevleriniz</h3>
              <p className="text-sm text-blue-700 mt-1">
                Ürün resimlerini harici sisteminizde yükleyin. 
                Ardından bu panelde "Tamamlandı" butonuna tıklayarak işlemi onaylayın.
              </p>
            </div>
          </div>
        </div>

        {/* Ürün Akışı Kartları */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'draft' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('draft')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ürün Girişi</CardTitle>
              <Badge variant="secondary">{stats.draft}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'nebim' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('nebim')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Nebim İşlemleri</CardTitle>
              <Badge variant="secondary">{stats.nebim}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('nebim') || 'Nebim'}</p>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'barcode_sent' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('barcode_sent')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Üreticiye Gönderim</CardTitle>
              <Badge variant="secondary">{stats.barcode_sent}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'stock_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('stock_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Stok Girişi</CardTitle>
              <Badge variant="secondary" className="bg-orange-600 text-white">{stats.stock_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('warehouse') || 'Depo'}</p>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'api_transfer' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('api_transfer')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ticimaxa Aktarım</CardTitle>
              <Badge variant="secondary" className="bg-blue-600 text-white">{stats.api_transfer}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'content_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('content_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">İçerik</CardTitle>
              <Badge variant="secondary">{stats.content_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('content') || 'İçerik'}</p>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'waiting_image' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('waiting_image')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Görsel Bekleyen</CardTitle>
              <Badge variant="secondary" className="bg-purple-600 text-white">{stats.waiting_image}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('visual') || 'Görsel'}</p>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'published' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('published')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayınlanan</CardTitle>
              <Badge variant="secondary" className="bg-green-600 text-white">{stats.published}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('all')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Tüm Ürünler</CardTitle>
              <Badge variant="secondary" className="bg-blue-600 text-white">{stats.total}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Filtreyi temizle</p>
            </CardContent>
          </Card>
        </div>

        {/* Aktif Filtre Göstergesi */}
        {filters.statusFilter && (
          <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
            <span className="text-sm font-medium">Aktif Filtre:</span>
            <Badge variant="outline" className="gap-1">
              {filters.statusFilter === 'draft' && 'Ürün Girişi'}
              {filters.statusFilter === 'nebim' && 'Nebim İşlemleri'}
              {filters.statusFilter === 'barcode_sent' && 'Üreticiye Gönderim'}
              {filters.statusFilter === 'stock_entry' && 'Stok Girişi'}
              {filters.statusFilter === 'api_transfer' && 'Ticimaxa Aktarım'}
              {filters.statusFilter === 'content_entry' && 'İçerik'}
              {filters.statusFilter === 'waiting_image' && 'Görsel Bekleyen'}
              {filters.statusFilter === 'published' && 'Yayınlanan'}
              {filters.statusFilter === 'all' && 'Tüm Ürünler'}
              <button 
                onClick={() => setFilters(prev => ({ ...prev, statusFilter: '' }))}
                className="ml-1 hover:bg-gray-200 rounded-full p-1"
              >
                ✕
              </button>
            </Badge>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExcelExport} className="gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Excel'e Aktar
            </Button>
          </div>
        </div>
          
        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <FilterBar 
              filters={filters}
              onFilterChange={handleFilterChange}
              products={products}
            />
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Ürün bulunamadı</p>
              </div>
            ) : (
              <ProductTable 
                products={filteredProducts}
                onRefresh={loadProducts}
                onImageUpload={handleImageUpload}
                onReject={handleReject}
                onClosureRequest={handleClosureRequest}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default VisualDashboard;
